import React, { useEffect, useState, useCallback } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import api from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { currentIsoWeek } from '@/lib/constants';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, CartesianGrid } from 'recharts';
import { RoleBadge } from '@/components/common/RoleBadge';

export default function WeeklyDashboard() {
  const [week, setWeek] = useState(currentIsoWeek());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (w) => {
    setLoading(true);
    try {
      const r = await api.get('/dashboard/weekly', { params: { week: w } });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(week); }, [week, load]);

  const chartData = (data?.aggregates || []).map((a) => ({ name: a.name?.split(' ')[0] || a.user_id, Completed: a.completed, Pending: a.pending, Hours: a.hours }));

  const blockers = (data?.aggregates || []).filter((a) => a.blockers).map((a) => ({ name: a.name, text: a.blockers }));

  return (
    <AppShell title="Weekly Progress">
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <Input type="week" value={week} onChange={(e) => setWeek(e.target.value)} className="max-w-[220px]" data-testid="weekly-week-selector" />
          <div className="text-sm text-muted-foreground">{data?.week_label || 'Loading…'}</div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          {['users', 'completed', 'pending', 'hours'].map((k) => (
            <Card key={k}>
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{k}</div>
                <div className="text-3xl font-display font-semibold mt-1 tabular-nums">{data?.totals?.[k] ?? '–'}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {blockers.length > 0 && (
          <Card className="border-[hsl(28_78%_52%_/_0.4)]" data-testid="weekly-blockers-callout">
            <CardContent className="p-4 flex gap-3">
              <AlertTriangle className="h-5 w-5 text-[hsl(28_78%_40%)] shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">Blockers reported</div>
                <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
                  {blockers.map((b) => (<li key={b.name}><span className="text-foreground font-medium">{b.name}:</span> {b.text}</li>))}
                </ul>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <Card data-testid="weekly-chart-tasks">
            <CardHeader><CardTitle className="font-display">Tasks: completed vs pending</CardTitle></CardHeader>
            <CardContent style={{ height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={12} stroke="hsl(var(--muted-foreground))" />
                  <YAxis fontSize={12} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))' }} />
                  <Legend />
                  <Bar dataKey="Completed" fill="hsl(var(--chart-4))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Pending" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card data-testid="weekly-chart-hours">
            <CardHeader><CardTitle className="font-display">Hours logged</CardTitle></CardHeader>
            <CardContent style={{ height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={12} stroke="hsl(var(--muted-foreground))" />
                  <YAxis fontSize={12} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))' }} />
                  <Bar dataKey="Hours" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader><CardTitle className="font-display">Per user breakdown</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="weekly-per-user-table">
              <thead className="bg-secondary/60"><tr><th className="text-left p-2">User</th><th className="text-left p-2">Role</th><th className="text-left p-2">Completed</th><th className="text-left p-2">Pending</th><th className="text-left p-2">Hours</th></tr></thead>
              <tbody>
                {(data?.aggregates || []).map((a) => (
                  <tr key={a.user_id} className="border-t"><td className="p-2 font-medium">{a.name}</td><td className="p-2"><RoleBadge role={a.role} /></td><td className="p-2">{a.completed}</td><td className="p-2">{a.pending}</td><td className="p-2">{a.hours}</td></tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
